import { useState } from 'react'
import { useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { WizardChrome, WizardFooter } from './WizardLayout'
import { connectionFor, detailOf, pathFor, stepStatus } from './steps'

const PROVIDER = 'linkedin'

/**
 * Step 3 — S-D2's `s3` and `s3-fail`.
 *
 * **The risk is stated in plain language and the limits are numbers.** Automating
 * LinkedIn breaches its User Agreement and accounts get restricted; a tenant who
 * is surprised by that later is a tenant AugRank misled. The three figures are
 * the ones CLAUDE.md hard-enforces in code — 50 searches, 20 actions, and zero
 * retries after a challenge — so they are a promise, not marketing.
 *
 * The checkbox gates the button *and* the server refuses without it. Both, on
 * purpose: the button is the courtesy and the server check is the rule, and §8
 * needs the acknowledgement recorded with the version of the text that was
 * shown — which only the server can do.
 *
 * A `challenge` outcome is the screen that matters most. AugRank stopped and did
 * not try again, and this says so in those words, because the reason accounts
 * get banned is retrying through a checkpoint.
 */
export function LinkedInStep(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const connection = connectionFor(state, PROVIDER)
  const step = stepStatus(state, 'linkedin')
  const [acknowledged, setAcknowledged] = useState(connection?.acknowledged_at != null)

  const connect = useMutation((body: { acknowledged: boolean }) =>
    call(() => api.POST('/api/v1/onboarding/linkedin/connect', { body })),
  )

  async function onConnect(): Promise<void> {
    const result = await connect.run({ acknowledged })
    if (!result.ok) return
    adopt(result.data.state)
    if (result.data.redirect_url) {
      window.location.assign(result.data.redirect_url)
      return
    }
    if (result.data.status === 'connected') void navigate(pathFor(result.data.state.current_step))
  }

  if (connection?.status === 'challenge') {
    return (
      <div className="app-wiz">
        <WizardChrome current="linkedin" />
        <Challenge
          detail={connection.last_error}
          busy={connect.pending}
          onReconnect={() => {
            void onConnect()
          }}
          onContinue={() => {
            void navigate(pathFor('sender'))
          }}
        />
      </div>
    )
  }

  return (
    <div className="app-wiz">
      <WizardChrome current="linkedin" />

      <section className="ar-card">
        <div className="ar-card__body ar-stack">
          <div>
            <h2 className="ar-h2">Connect LinkedIn for lead discovery</h2>
            <p className="ar-muted" style={{ margin: 'var(--ar-space-2) 0 0' }}>
              Entirely optional. SEO works without it — this only powers outreach.
            </p>
          </div>

          <InlineError failure={connect.state.failure} />

          <div className="ar-alert ar-alert--warning">
            <span className="ar-alert__icon" aria-hidden="true">
              !
            </span>
            <div className="ar-alert__body">
              <strong>Read this before connecting</strong>
              Automating LinkedIn breaches LinkedIn&rsquo;s User Agreement. Accounts can be
              restricted or permanently banned. We reduce the risk but cannot remove it.
            </div>
          </div>

          <div className="ar-panel ar-stack ar-stack--tight">
            <strong>What we enforce, in code</strong>
            <div className="ar-grid ar-grid--3">
              <Limit value="50" label="searches / day max" />
              <Limit value="20" label="actions / day max" />
              <Limit value="0" label="retries after a challenge" />
            </div>
            <p
              className="ar-muted"
              style={{ margin: 'var(--ar-space-2) 0 0', fontSize: 'var(--ar-text-body-sm-size)' }}
            >
              Randomised human-like delays. A dedicated residential IP for your account only. If
              LinkedIn shows any checkpoint, we pause immediately and email you — we never try to
              work through it.
            </p>
          </div>

          <label className="ar-choice ar-choice--check">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => {
                setAcknowledged(event.target.checked)
                connect.reset()
              }}
            />
            <span className="ar-choice__box" aria-hidden="true" />
            <span className="ar-choice__text">
              I understand this breaches LinkedIn&rsquo;s terms and that my account could be
              restricted or banned. I accept that risk.
              <span className="ar-choice__desc">
                Required. We record the time and the exact wording you were shown.
              </span>
            </span>
          </label>

          {connection?.status === 'connected' ? (
            <div className="ar-alert ar-alert--success" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                ✓
              </span>
              <div className="ar-alert__body">
                <strong>Connected</strong>
                {connection.account_label ?? 'Lead discovery is ready to run.'}
              </div>
            </div>
          ) : step?.status === 'failed' && detailOf(step).message ? (
            <div className="ar-alert ar-alert--danger" role="alert">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>We could not connect LinkedIn</strong>
                {String(detailOf(step).message)}
              </div>
            </div>
          ) : null}
        </div>

        <WizardFooter current="linkedin">
          <Button
            tone="primary"
            disabled={!acknowledged}
            busy={connect.pending}
            busyLabel="Connecting LinkedIn"
            onClick={() => {
              void onConnect()
            }}
          >
            Connect LinkedIn
          </Button>
        </WizardFooter>
      </section>
    </div>
  )
}

function Limit({ value, label }: { value: string; label: string }): JSX.Element {
  return (
    <div>
      <div className="ar-h2 ar-num">{value}</div>
      <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
        {label}
      </span>
    </div>
  )
}

/** S-D2's `s3-fail`. Paused, never retried — that is the whole message. */
function Challenge({
  detail,
  busy,
  onReconnect,
  onContinue,
}: {
  detail: string | null | undefined
  busy: boolean
  onReconnect: () => void
  onContinue: () => void
}): JSX.Element {
  return (
    <>
      <section className="ar-card">
        <div className="ar-card__body">
          <div className="ar-error-state" role="alert">
            <div className="ar-error-state__icon" aria-hidden="true">
              !
            </div>
            <p className="ar-error-state__title">LinkedIn asked for a security check</p>
            <p className="ar-error-state__body">
              We stopped immediately and did not try again — retrying through a checkpoint is what
              gets accounts banned. Open LinkedIn in your own browser, complete the check, then come
              back and reconnect.
            </p>
            {detail ? <p className="ar-error-state__detail">{detail}</p> : null}
            <div className="ar-btn-group" style={{ justifyContent: 'center' }}>
              <Button
                tone="primary"
                size="sm"
                busy={busy}
                busyLabel="Reconnecting"
                onClick={onReconnect}
              >
                I&rsquo;ve completed it — reconnect
              </Button>
              <Button size="sm" onClick={onContinue}>
                Continue without LinkedIn
              </Button>
            </div>
          </div>
        </div>
      </section>

      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>Everything else keeps running</strong>A LinkedIn pause never stops your SEO work.
          Audits, content, and publishing carry on.
        </div>
      </div>
    </>
  )
}
